import type { ColumnDef } from '@tanstack/react-table';
import { createAlphabeticalSortingFunction } from '@src/design-system/molecules/ticlick-table/table-cells/utils/sorting';

import type { TUniversity } from '../types';
import type { TSubmission } from '@src/shared/types/submission';
import { UniversityStatusCell } from './cells/university-status-cell';
import { LabelAction } from '../pages/list/actions/label-action';
import { ResponseBagde } from '@src/features/programs/pages/list/-components/response-bagde';
import { generateCustomFieldColumns } from '@src/shared/utils/helpers/generate-custom-field-columns';
import UniversityAssigneeListCell from './cells/university-assignee-list-cell';

export const universitiesColumns = (data: TUniversity[]): ColumnDef<TUniversity>[] => {
    return [
        {
            id: 'name',
            accessorKey: 'name',
            header: 'University Name',
            sortingFn: createAlphabeticalSortingFunction('name'),
            meta: {
                isCopy: true,
            },
            cell: ({ row }) => <span>{row.original.name ?? '-'}</span>,
        },
        {
            id: 'status',
            accessorKey: 'status',
            header: 'Status',
            sortingFn: createAlphabeticalSortingFunction('status'),
            cell: ({ row }) => <UniversityStatusCell universityId={row.original.id} status={row.original.status} />
        },
        {
            id: 'account_manager',
            accessorKey: 'manager_member',
            sortingFn: createAlphabeticalSortingFunction('manager_member.first_name'),
            header: 'Account Manager',
            cell: (props) => {
                const { row } = props;

                const submission = {
                    ...(row.original as unknown as TSubmission),
                    id: row.original.id,
                };

                return (
                    <UniversityAssigneeListCell submission={submission} />
                );
            },
        },
        {
            id: 'website',
            accessorKey: 'website',
            header: 'Website',
            sortingFn: createAlphabeticalSortingFunction('website'),
            meta: {
                isCopy: true,
            },
            cell: ({ row }) => <span className="text-primary-1000 text-sm ">{row.original.website ?? '-'}</span>
        },
        {
            id: 'city',
            accessorKey: 'city',
            header: 'City',
            sortingFn: createAlphabeticalSortingFunction('city'),
            cell: ({ row }) => <span>{row.original.city ?? '-'}</span>
        },
        {
            id: 'country',
            accessorKey: 'country',
            header: 'Country',
            sortingFn: createAlphabeticalSortingFunction('country'),
            cell: ({ row }) => <span>{row.original.country?.name ?? '-'}</span>
        },
        {
            id: 'contact_name',
            accessorKey: 'main_contact_name',
            header: 'Contact Name',
            sortingFn: createAlphabeticalSortingFunction('main_contact_name'),
            cell: ({ row }) => <span>{row.original.main_contact_name ?? '-'}</span>
        },
        {
            id: 'main_contact_email',
            accessorKey: 'main_contact_email',
            sortingFn: createAlphabeticalSortingFunction('main_contact_email'),
            header: 'Email',
            meta: {
                isCopy: true,
            },
            cell: ({ row }) => <span>{row.original.main_contact_email ?? '-'}</span>
        },
        {
            id: 'main_contact_phone',
            accessorKey: 'main_contact_phone',
            sortingFn: createAlphabeticalSortingFunction('main_contact_phone'),
            header: 'Phone Number',
            meta: {
                isCopy: true,
            },
            cell: ({ row }) => <span>{row.original.main_contact_phone ?? '-'}</span>
        },
        {
            id: 'no_of_leads',
            accessorKey: 'no_of_leads',
            header: 'Leads',
            sortingFn: createAlphabeticalSortingFunction('no_of_leads'),
            cell: ({ row }) => <ResponseBagde count={row.original.no_of_leads ?? 0} />
        },
        {
            id: 'no_of_participants',
            accessorKey: 'no_of_participants',
            sortingFn: createAlphabeticalSortingFunction('no_of_participants'),
            header: 'Participants',
            cell: ({ row }) => <ResponseBagde count={row.original.no_of_participants ?? 0} />
        },
        // {
        //     id: 'conversion_rate',
        //     accessorKey: 'conversion_rate',
        //     sortingFn: createAlphabeticalSortingFunction('conversion_rate'),
        //     header: 'Conversion Rate',
        //     cell: ({ row }) => <span>{row.original.conversion_rate ?? '-'}</span>
        // },
        {
            id: 'labels',
            accessorKey: 'labels',
            header: ' Labels',
            cell: (props) => {
                const { row, getValue } = props;
                return (
                    <LabelAction
                        labels={(getValue() as []) ?? []}
                        id={row.original.id}
                    />
                );
            },
        },
        ...generateCustomFieldColumns(data ?? []),

    ]
}

